import { useContext } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import AppContext from './AppContext';
import Page from './Page';
import About from './views/About';
import Admin from './views/Admin';
import AI from './views/Apps/AI';
import Blog from './views/Blog';
import Chat from './views/Chat';
import Community from './views/Community';
import Dashboard from './views/Apps/Dashboard';
import DoNow from './views/Apps/DoNow';
import Donate from './views/Donate';
import Events from './views/Events';
import EventPage from './views/Events/EventPage';
import NewEvent from './views/Events/NewEvent';
import Games from './views/Games';
import HexTones from './views/Games/HexTones';
import Chess from './views/Games/Chess';
import ChessGPT from './views/Games/ChessGPT';
import Chiv from './views/Games/Chiv';
import Cultivation from './views/Games/Cultivation';
import FightSim from './views/Games/FightSim';
import HappyIslandAdventureGame from './views/Games/HappyIslandAdventure/HappyIsland';
import IncrementalClickerGame from './views/Games/IncrementalClicker';
import Jackbox from './views/Games/Jackbox';
import SimCity from './views/Games/SimCity';
import TicTacToe from './views/Games/TicTacToe';
import TimeBalls from './views/Games/TimeBalls';
import ILU2 from './views/ilu2';
import Index from './views/Index';
import InviteCodes from './views/InviteCodes';
import { ListsPage } from './views/lists/src';
import LifeOptimizer from './views/Apps/LifeOptimizer';
import Names from './views/Apps/Names';
import PageDoesNotExist from './views/PageDoesNotExist';
import PremiumPage from './views/Premium';
import PrivatePosts from './views/Private/Posts';
import PS from './views/PS';
import Queues from './views/Queues';
import Rooms from './views/Rooms';
import Room from './views/Rooms/Room';
import ScoreKeeper from './views/Apps/ScoreKeeper';
import Settings from './views/Settings';
import Sitemap from './views/Sitemap';
import Spinner from './shared/Spinner';
import Training from './views/Training';
import UserProfile from './views/UserProfile';
import Write from './views/Write';

const Routes = () => {
  const { loadingUser, user } = useContext(AppContext);
  const location = useLocation();

  if (loadingUser) {
    return <Spinner size="lg" />;
  }

  return (
    <Switch>
      {/* Public Routes */}
      <Route
        exact
        path="/"
        key={location.pathname}
        render={(props) => (
          <Page title="xBook" loginRequired={false}>
            <Index {...props} />
          </Page>
        )}
      />
      <Route
        path="/about"
        render={(props) => (
          <Page title="About | xBook">
            <About {...props} />
          </Page>
        )}
      />
      <Route
        path="/blog"
        render={(props) => (
          <Page title="Blog | xBook">
            <Blog {...props} />
          </Page>
        )}
      />
      {/* Redirect old post links to general post link */}
      <Route
        path="/posts/:postId"
        render={(props) => (
          <Redirect to={`/r/general/posts/${props.match.params.postId}`} />
        )}
      />
      <Route path="/chat">
        <Chat />
      </Route>
      <Route
        path={['/cluesweeper', '/clue-sweeper', '/codenames', '/code-names']}
        render={() => (
          <Page title="Clue Sweeper has moved!" loginRequired={false}>
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <h1>Looking for Clue Sweeper?</h1>
              <p style={{ fontSize: '1.2rem', marginTop: '1rem' }}>
                Our Codenames-style game now lives at:
              </p>
              <a
                href="https://cluesweeper.com"
                style={{ fontSize: '1.5rem', fontWeight: 'bold' }}
              >
                cluesweeper.com
              </a>
            </div>
          </Page>
        )}
      />
      <Route
        path="/donate"
        render={(props) => (
          <Page title="Donate | xBook">
            <Donate {...props} />
          </Page>
        )}
      />
      <Route
        path="/games"
        render={(props) => (
          <Page title="Games | xBook">
            <Games {...props} />
          </Page>
        )}
      />
      <Route path="/ilu2">
        <Page>
          <ILU2 />
        </Page>
      </Route>
      <Route
        path="/ps"
        render={(props) => (
          <Page loginRequired={false} title="PS | xBook">
            <PS {...props} />
          </Page>
        )}
      />
      <Route
        path="/sitemap"
        render={(props) => (
          <Page title="Sitemap | xBook">
            <Sitemap {...props} />
          </Page>
        )}
      />

      {/* Admin Routes - Return 404 for non-admins to avoid information disclosure */}
      <Route
        path="/admin"
        render={(props) => {
          if (!user?.admin) {
            return (
              <Page title="404 | xBook">
                <PageDoesNotExist {...props} />
              </Page>
            );
          }
          return (
            <Page title="Admin | xBook">
              <Admin {...props} />
            </Page>
          );
        }}
      />
      <Route
        path="/ai"
        render={(props) => {
          if (!user?.admin) {
            return (
              <Page title="404 | xBook">
                <PageDoesNotExist {...props} />
              </Page>
            );
          }
          return (
            <Page title="AI | xBook">
              <AI {...props} />
            </Page>
          );
        }}
      />

      {/* Authenticated Routes - Always render, Page component handles auth */}
      <Route
        path="/chess"
        render={(props) => (
          <Page title="Chess | xBook">
            <Chess {...props} />
          </Page>
        )}
      />
      <Route
        path="/chessgpt"
        render={(props) => (
          <Page title="ChessGPT | xBook">
            <ChessGPT {...props} />
          </Page>
        )}
      />
      <Route
        path="/chiv"
        render={(props) => (
          <Page title="Chiv | xBook">
            <Chiv {...props} />
          </Page>
        )}
      />
      <Route
        path="/community"
        render={(props) => (
          <Page title="Community | xBook">
            <Community {...props} />
          </Page>
        )}
      />
      <Route
        path="/cultivation"
        render={(props) => (
          <Page title="Cultivation Game | xBook">
            <Cultivation {...props} />
          </Page>
        )}
      />
      <Route
        path="/dashboard"
        render={(props) => (
          <Page title="Dashboard | xBook">
            <Dashboard {...props} />
          </Page>
        )}
      />
      <Route
        path="/donow"
        render={(props) => (
          <Page title="Do Now">
            <DoNow {...props} />
          </Page>
        )}
      />
      <Route
        path="/events"
        exact
        render={(props) => (
          <Page title="Events | xBook" loginRequired={false}>
            <Events {...props} />
          </Page>
        )}
      />
      <Route
        path="/events/:eventId"
        render={(props) => (
          <Page title="Event | xBook">
            <EventPage {...props} />
          </Page>
        )}
      />
      <Route
        path="/events/new"
        exact
        render={(props) => (
          <Page title="New Event | xBook">
            <NewEvent {...props} />
          </Page>
        )}
      />
      <Route
        path="/fight-sim"
        exact
        render={(props) => (
          <Page title="Fight Sim | xBook">
            <FightSim />
          </Page>
        )}
      />
      <Route
        path="/happy-island"
        exact
        render={(props) => (
          <Page title="Happy Island Adventure Game | xBook">
            <HappyIslandAdventureGame />
          </Page>
        )}
      />
      <Route
        path="/hextones"
        render={(props) => (
          <Page title="Hex Tones | xBook">
            <HexTones {...props} />
          </Page>
        )}
      />
      <Route
        path="/incremental-clicker-game"
        render={(props) => (
          <Page title="Incremental Clicker Game | xBook">
            <IncrementalClickerGame {...props} />
          </Page>
        )}
      />
      <Route
        path="/invitecodes"
        render={(props) => (
          <Page title="Invite Codes | xBook">
            <InviteCodes {...props} />
          </Page>
        )}
      />
      <Route
        path="/jackbox"
        render={(props) => (
          <Page title="Jackbox | xBook">
            <Jackbox {...props} />
          </Page>
        )}
      />
      <Route
        path="/lifeoptimizer"
        render={(props) => (
          <Page title="Life Optimizer | xBook">
            <LifeOptimizer {...props} />
          </Page>
        )}
      />
      <Route
        path="/lists"
        render={(props) => (
          <Page title="Lists | xBook">
            <ListsPage {...props} />
          </Page>
        )}
      />
      <Route
        path="/names"
        render={(props) => (
          <Page title="Names | xBook">
            <Names {...props} />
          </Page>
        )}
      />
      <Route
        path="/premium"
        render={(props) => (
          <Page title="Premium | xBook">
            <PremiumPage {...props} />
          </Page>
        )}
      />
      <Route
        path="/private"
        exact
        render={(props) => (
          <Page>
            <PrivatePosts {...props} />
          </Page>
        )}
      />
      <Route
        path="/private/posts"
        exact
        render={(props) => <Redirect to="/private" />}
      />
      <Route
        path="/public"
        exact
        render={(props) => (
          <Page title="Public | xBook">
            <Room {...props} />
          </Page>
        )}
      />
      <Route
        path="/queues"
        render={(props) => (
          <Page title="Queues | xBook">
            <Queues {...props} />
          </Page>
        )}
      />
      <Route
        path="/r"
        exact
        render={(props) => (
          <Page title="Rooms | xBook">
            <Rooms {...props} />
          </Page>
        )}
      />
      <Route
        path="/r/:roomId"
        exact
        render={(props) => (
          <Page>
            <Room {...props} />
          </Page>
        )}
      />
      <Route
        path="/r/:roomId/posts/:postId"
        render={(props) => (
          <Page title="xBook">
            <Room {...props} />
          </Page>
        )}
      />
      <Route
        path="/rooms"
        render={(props) => (
          <Page title="Rooms | xBook">
            <Rooms {...props} />
          </Page>
        )}
      />
      <Route
        path="/scorekeeper"
        render={(props) => (
          <Page title="Score Keeper | xBook">
            <ScoreKeeper {...props} />
          </Page>
        )}
      />
      <Route
        path="/settings"
        render={(props) => (
          <Page title="Settings | xBook">
            <Settings {...props} />
          </Page>
        )}
      />
      <Route
        path="/simcity"
        render={(props) => (
          <Page title="Sim City | xBook">
            <SimCity {...props} />
          </Page>
        )}
      />
      <Route
        path="/tictactoe"
        render={(props) => (
          <Page title="Pente | xBook">
            <TicTacToe {...props} />
          </Page>
        )}
      />
      <Route
        path="/time-balls"
        render={(props) => (
          <Page title="Time Balls | xBook">
            <TimeBalls {...props} />
          </Page>
        )}
      />
      <Route
        path="/training"
        exact
        render={(props) => (
          <Page title="Training | xBook">
            <Training {...props} />
          </Page>
        )}
      />
      <Route
        path="/training/:modalityId"
        render={(props) => (
          <Page title="Training | xBook">
            <Training {...props} />
          </Page>
        )}
      />
      <Route
        path="/u/:userId"
        render={(props) => (
          <Page>
            <UserProfile {...props} />
          </Page>
        )}
      />
      <Route
        path="/write"
        exact
        render={(props) => (
          <Page title="Write | xBook">
            <Write {...props} />
          </Page>
        )}
      />

      {/* 404 Route */}
      <Route
        render={(props) => (
          <Page title="404 | xBook">
            <PageDoesNotExist {...props} />
          </Page>
        )}
      />
    </Switch>
  );
};

export default Routes;
